import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import moment from 'moment';

import {
  IcVisibility,
  IcPencil,
  IcPrint,
  IcDescriptionXml,
} from '@/assets/images';
import { DATE_FORMAT } from '@/config';
import { DATA_WORKS_STATUS_MAP } from '@/constants/arrest';
import { IArrestee, IOffenseOption, IOffenseOptionItem } from '@/models';

import { getOffenseOptionItems } from '../KaseCrimes/utilities';

const number = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.number || '-',
});

const arrestId = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.arrestId || '-',
  valueGetter: (_, row) => row.arrestId || '-',
});

const masterName = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row.fullName || '-';
  },
  valueGetter: (_, row) => row.fullName,
});

const arrestDate = (): Partial<GridColDef> => ({
  renderCell: (params) =>
    params.row.arrestDate
      ? moment(params.row.arrestDate).format(DATE_FORMAT)
      : '-',
});

const dataWorksStatus = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const status = params.row.dataWorksStatus;
    return status ? DATA_WORKS_STATUS_MAP[status] || status : '-';
  },
  valueGetter: (_, row) => {
    const status = row.dataWorksStatus;
    return status ? DATA_WORKS_STATUS_MAP[status] || status : '-';
  },
});

const actions = (
  onView: ((arrestee: IArrestee) => void) | undefined,
  onEdit: ((arrestee: IArrestee) => void) | undefined,
  onPrint: ((arrestee: IArrestee) => void) | undefined,
  isReadOnly: boolean | undefined,
  onExport: ((arrestee: IArrestee) => void) | undefined,
  kaseNumber?: string,
  isPrinting?: boolean,
  printingArresteeId?: string | null,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  width: 200,
  renderCell: (params) => {
    const arrestee = params.row as IArrestee;
    // Validation: check if required fields are populated
    const hasArrestDate = !!arrestee.arrestDate;
    const hasKaseNumber = !!kaseNumber;
    const hasExistingStatus = !!arrestee.dataWorksStatus;
    const isExportEnabled =
      hasArrestDate && hasKaseNumber && !hasExistingStatus;

    let tooltipMessage = 'Export to DataWorks';
    if (hasExistingStatus) {
      tooltipMessage = 'Export already initiated';
    } else if (!hasArrestDate || !hasKaseNumber) {
      tooltipMessage = 'Arrest date is required';
    }

    return (
      <>
        <IconButton
          aria-label="visibility"
          onClick={(e) => {
            e.stopPropagation();
            onView?.(params.row);
          }}
        >
          <IcVisibility />
        </IconButton>
        {!isReadOnly && (
          <IconButton
            aria-label="edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(params.row);
            }}
          >
            <IcPencil />
          </IconButton>
        )}
        {!isReadOnly && (
          <IconButton
            aria-label="print"
            disabled={!!isPrinting}
            onClick={(e) => {
              e.stopPropagation();
              onPrint?.(params.row);
            }}
          >
            {isPrinting && printingArresteeId === params.row?._id ? (
              <CircularProgress size={18} />
            ) : (
              <IcPrint />
            )}
          </IconButton>
        )}
        <Tooltip title={tooltipMessage}>
          <span>
            <IconButton
              aria-label="export-to-dataworks"
              disabled={!isExportEnabled}
              onClick={(e) => {
                e.stopPropagation();
                onExport?.(arrestee);
              }}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4,
                },
              }}
            >
              <IcDescriptionXml />
            </IconButton>
          </span>
        </Tooltip>
      </>
    );
  },
});

export const kaseArrestCols = (
  onView?: (arrestee: IArrestee) => void,
  onEdit?: (arrestee: IArrestee) => void,
  onPrint?: (arrestee: IArrestee) => void,
  isReadOnly?: boolean,
  filteredOffenseOptions?: IOffenseOption[],
  onExport?: (arrestee: IArrestee) => void,
  kaseNumber?: string,
  isPrinting?: boolean,
  printingArresteeId?: string | null,
) => {
  const ArrestTypeLabel = getOffenseOptionItems(
    'arrestType',
    filteredOffenseOptions ?? [],
  ) as Map<string, IOffenseOptionItem>;
  const arrestType = (): Partial<GridColDef> => ({
    renderCell: (params) => {
      return (
        (!!ArrestTypeLabel.size &&
          ArrestTypeLabel.get(params.row.arrestType)?.label) ||
        '-'
      );
    },
  });

  const ArrestStatusLabel = getOffenseOptionItems(
    'arrestStatus',
    filteredOffenseOptions ?? [],
  ) as Map<string, IOffenseOptionItem>;

  const arrestStatus = (): Partial<GridColDef> => ({
    renderCell: (params) =>
      (!!ArrestStatusLabel.size &&
        ArrestStatusLabel.get(params.row.arrestStatus)?.label) ||
      '-',
  });

  return {
    number,
    arrestId,
    masterName,
    arrestDate,
    arrestType,
    arrestStatus,
    dataWorksStatus,
    actions: () =>
      actions(
        onView,
        onEdit,
        onPrint,
        isReadOnly,
        onExport,
        kaseNumber,
        isPrinting,
        printingArresteeId,
      ),
  };
};
